<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-lg-12">
        <div class="d-flex flex-wrap flex-wrap align-items-center justify-content-between">
          <div>
            <h4 class="mb-3">{{ title }}</h4>
            <p class="mb-0">{{ desc1 }}<br> {{ desc2 }}</p>
          </div>
          <div class="d-flex flex-column">
            <b-button variant="primary pl-5 pr-5" class="mt-2 mr-1" @click="refreshData">데이터 새로고침</b-button>
            <b-form-select v-model="selectedPeriod" :options="periodOptions" class="mt-2 mr-1"></b-form-select>
          </div>
        </div>
      </div>
      <div class="col-lg-12">
        <img v-if="showspinner" style="max-width:100%;" :src="require('@/assets/images/small/spinner.gif')">
      </div>
    </div>

    <!-- 대시보드 섹션 -->
    <div class="row mt-3" v-if="!showspinner && showDashboard">
      <div class="col-md-3">
        <b-card bg-variant="primary" text-variant="white" class="text-center">
          <h5>총 광고비</h5>
          <h3>{{ formatCurrency(totalAdCost) }}</h3>
          <p>전월 대비 {{ adCostGrowth > 0 ? '+' : '' }}{{ adCostGrowth }}%</p>
        </b-card>
      </div>
      <div class="col-md-3">
        <b-card bg-variant="success" text-variant="white" class="text-center">
          <h5>ROAS</h5>
          <h3>{{ roas }}%</h3>
          <p>전월 대비 {{ roasGrowth > 0 ? '+' : '' }}{{ roasGrowth }}%</p>
        </b-card>
      </div>
      <div class="col-md-3">
        <b-card bg-variant="info" text-variant="white" class="text-center">
          <h5>총 클릭수</h5>
          <h3>{{ totalClicks }}</h3>
          <p>전월 대비 {{ clicksGrowth > 0 ? '+' : '' }}{{ clicksGrowth }}%</p>
        </b-card>
      </div>
      <div class="col-md-3">
        <b-card bg-variant="warning" text-variant="white" class="text-center">
          <h5>평균 CTR</h5>
          <h3>{{ avgCtr }}%</h3>
          <p>전월 대비 {{ ctrGrowth > 0 ? '+' : '' }}{{ ctrGrowth }}%</p>
        </b-card>
      </div>
    </div>

    <!-- 광고 성과 추이 차트 -->
    <div class="row mt-3" v-if="!showspinner && showDashboard">
      <div class="col-lg-12">
        <b-card no-body>
          <b-card-header>
            <h5 class="mb-0">광고 성과 추이</h5>
          </b-card-header>
          <b-card-body>
            <ApexChart ref="adPerformanceChart" element="ad-performance-chart" :chartOption="adPerformanceChartOption" />
          </b-card-body>
        </b-card>
      </div>
    </div>

    <!-- 광고 유형별 성과 -->
    <div class="row mt-3" v-if="!showspinner && showDashboard">
      <div class="col-md-6">
        <b-card no-body>
          <b-card-header>
            <h5 class="mb-0">광고 유형별 비용</h5>
          </b-card-header>
          <b-card-body>
            <ApexChart ref="adTypePieChart" element="ad-type-pie-chart" :chartOption="adTypePieChartOption" />
          </b-card-body>
        </b-card>
      </div>
      <div class="col-md-6">
        <b-card no-body>
          <b-card-header>
            <h5 class="mb-0">광고 유형별 ROAS</h5>
          </b-card-header>
          <b-card-body>
            <ApexChart ref="adTypeRoasChart" element="ad-type-roas-chart" :chartOption="adTypeRoasChartOption" />
          </b-card-body>
        </b-card>
      </div>
    </div>

    <!-- 키워드 분석 -->
    <div class="row mt-3" v-if="!showspinner && showDashboard">
      <div class="col-lg-12">
        <b-card no-body>
          <b-card-header class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0">키워드 분석</h5>
            <div>
              <b-form-select v-model="keywordSortBy" :options="keywordSortOptions" class="mr-2"></b-form-select>
              <b-form-input v-model="keywordSearch" placeholder="키워드 검색" class="d-inline-block" style="width: 200px;"></b-form-input>
            </div>
          </b-card-header>
          <b-card-body>
            <CDataTable
                storage-key="growthAdsStatus.1"
              :headers="keywordTableHeaders"
              :items="filteredKeywords"
              :per-page="10"
              :pagination="true"
              itemKey="키워드"
            >
              <template v-slot:성과추이="{ item }">
                <div style="width: 120px; height: 40px;">
                  <ApexChart :chartOption="getKeywordTrendChartOption(item)" :element="`keyword-trend-${item.키워드}`" />
                </div>
              </template>
              <template v-slot:상태="{ item }">
                <b-badge :variant="getKeywordStatusVariant(item)">{{ getKeywordStatusText(item) }}</b-badge>
              </template>
            </CDataTable>
          </b-card-body>
        </b-card>
      </div>
    </div>

    <!-- 상품별 광고 ROI -->
    <div class="row mt-3" v-if="!showspinner && showDashboard">
      <div class="col-lg-12">
        <b-card no-body>
          <b-card-header class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0">상품별 광고 ROI</h5>
            <div>
              <b-form-select v-model="productSortBy" :options="productSortOptions" class="mr-2"></b-form-select>
              <b-form-input v-model="productSearch" placeholder="상품명 검색" class="d-inline-block" style="width: 200px;"></b-form-input>
            </div>
          </b-card-header>
          <b-card-body>
            <CDataTable
                storage-key="growthAdsStatus.2"
              :headers="productTableHeaders"
              :items="filteredProducts"
              :per-page="10"
              :pagination="true"
              itemKey="vendorItemId"
            >
              <template v-slot:상품정보="{ item }">
                <div class="d-flex align-items-center">
                  <img :src="item.썸네일" alt="상품 이미지" style="width: 50px; height: 50px; object-fit: cover;" class="mr-2">
                  <div>
                    <p class="mb-0 font-weight-bold">{{ item.판매상품명 }}</p>
                    <small>{{ item.옵션명 }}</small>
                  </div>
                </div>
              </template>
              <template v-slot:ROI상태="{ item }">
                <b-badge :variant="getProductRoiVariant(item)">{{ getProductRoiText(item) }}</b-badge>
              </template>
            </CDataTable>
          </b-card-body>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import ApexChart from '@/components/charts/ApexChart';
import CDataTable from "@/components/common/CDataTable";

export default {
  name: 'growthAdsStatus',
  components: {
    ApexChart,
    CDataTable
  },
  data() {
    return {
      title: "광고현황",
      desc1: "광고 성과와 ROI를 분석하세요",
      desc2: "키워드별 성과와 상품별 광고 효율성을 확인하여 광고 전략을 최적화할 수 있습니다",
      showspinner: false,
      showDashboard: false,

      // 기간 선택
      selectedPeriod: '30days',
      periodOptions: [
        { value: '7days', text: '최근 7일' },
        { value: '30days', text: '최근 30일' },
        { value: '90days', text: '최근 90일' },
        { value: 'thisMonth', text: '이번 달' },
        { value: 'lastMonth', text: '지난 달' },
        { value: 'thisYear', text: '올해' }
      ],

      // 대시보드 데이터
      totalAdCost: 0,
      roas: 0,
      totalClicks: 0,
      avgCtr: 0,
      adCostGrowth: 0,
      roasGrowth: 0,
      clicksGrowth: 0,
      ctrGrowth: 0,

      // 광고 성과 추이 차트
      adPerformanceChartOption: null,

      // 광고 유형별 차트
      adTypePieChartOption: null,
      adTypeRoasChartOption: null,

      // 키워드 분석 데이터
      keywords: [],
      keywordSearch: '',
      keywordSortBy: 'clicks',
      keywordSortOptions: [
        { value: 'clicks', text: '클릭수 순' },
        { value: 'impressions', text: '노출수 순' },
        { value: 'ctr', text: 'CTR 순' },
        { value: 'cost', text: '비용 순' },
        { value: 'roas', text: 'ROAS 순' }
      ],
      keywordTableHeaders: [
        { text: '키워드', value: '키워드', align: 'left', width: 200 },
        { text: '노출수', value: '노출수', align: 'center', width: 100 },
        { text: '클릭수', value: '클릭수', align: 'center', width: 100 },
        { text: 'CTR', value: 'CTR', align: 'center', width: 100 },
        { text: '비용', value: '비용', align: 'center', width: 150 },
        { text: 'ROAS', value: 'ROAS', align: 'center', width: 100 },
        { text: '성과추이', value: '성과추이', align: 'center', width: 150, isSlot: true },
        { text: '상태', value: '상태', align: 'center', width: 100, isSlot: true }
      ],

      // 상품별 광고 ROI 데이터
      products: [],
      productSearch: '',
      productSortBy: 'roas',
      productSortOptions: [
        { value: 'roas', text: 'ROAS 순' },
        { value: 'cost', text: '광고비 순' },
        { value: 'revenue', text: '매출 순' },
        { value: 'clicks', text: '클릭수 순' }
      ],
      productTableHeaders: [
        { text: '상품정보', value: '상품정보', align: 'left', width: 300, isSlot: true },
        { text: '광고비', value: '광고비', align: 'center', width: 150 },
        { text: '매출', value: '매출', align: 'center', width: 150 },
        { text: 'ROAS', value: 'ROAS', align: 'center', width: 100 },
        { text: '클릭수', value: '클릭수', align: 'center', width: 100 },
        { text: 'CTR', value: 'CTR', align: 'center', width: 100 },
        { text: 'ROI상태', value: 'ROI상태', align: 'center', width: 100, isSlot: true }
      ],

      // 원본 데이터
      adData: [],
      dateLabels: []
    };
  },
  computed: {
    filteredKeywords() {
      let filtered = [...this.keywords];

      // 검색어 필터링
      if (this.keywordSearch) {
        const searchTerm = this.keywordSearch.toLowerCase();
        filtered = filtered.filter(item =>
          item.키워드.toLowerCase().includes(searchTerm)
        );
      }

      // 정렬
      filtered.sort((a, b) => {
        switch (this.keywordSortBy) {
          case 'clicks':
            return b.클릭수 - a.클릭수;
          case 'impressions':
            return b.노출수 - a.노출수;
          case 'ctr':
            return b.CTR - a.CTR;
          case 'cost':
            return b.비용 - a.비용;
          case 'roas':
            return b.ROAS - a.ROAS;
          default:
            return b.클릭수 - a.클릭수;
        }
      });

      return filtered;
    },

    filteredProducts() {
      let filtered = [...this.products];

      // 검색어 필터링
      if (this.productSearch) {
        const searchTerm = this.productSearch.toLowerCase();
        filtered = filtered.filter(item =>
          item.판매상품명.toLowerCase().includes(searchTerm) ||
          item.옵션명.toLowerCase().includes(searchTerm)
        );
      }

      // 정렬
      filtered.sort((a, b) => {
        switch (this.productSortBy) {
          case 'roas':
            return b.ROAS - a.ROAS;
          case 'cost':
            return b.광고비 - a.광고비;
          case 'revenue':
            return b.매출 - a.매출;
          case 'clicks':
            return b.클릭수 - a.클릭수;
          default:
            return b.ROAS - a.ROAS;
        }
      });

      return filtered;
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.showspinner = true;
      this.showDashboard = false;

      try {
        // 광고 데이터 로드
        const response = await axios.post('/api/ads/dashboard', {
          period: this.selectedPeriod
        });

        if (response.data.success) {
          this.adData = response.data.adData;
          this.dateLabels = response.data.dateLabels;

          // 대시보드 데이터 설정
          this.totalAdCost = response.data.totalAdCost;
          this.roas = response.data.roas;
          this.totalClicks = response.data.totalClicks;
          this.avgCtr = response.data.avgCtr;
          this.adCostGrowth = response.data.adCostGrowth;
          this.roasGrowth = response.data.roasGrowth;
          this.clicksGrowth = response.data.clicksGrowth;
          this.ctrGrowth = response.data.ctrGrowth;

          // 키워드 데이터 설정
          this.keywords = response.data.keywords;

          // 상품 데이터 설정
          this.products = response.data.products;

          // 차트 데이터 설정
          this.updateCharts();

          this.showDashboard = true;
        } else {
          // 데이터가 없는 경우 임시 데이터 생성
          this.generateDummyData();
        }
      } catch (error) {
        console.error('데이터 로드 오류:', error);
        // 오류 발생 시 임시 데이터 생성
        this.generateDummyData();
      } finally {
        this.showspinner = false;
      }
    },

    refreshData() {
      this.loadData();
    },

    updateCharts() {
      // 광고 성과 추이 차트 업데이트
      this.adPerformanceChartOption = {
        series: [
          {
            name: '광고비',
            type: 'column',
            data: this.adData.map(d => d.cost)
          },
          {
            name: 'ROAS',
            type: 'line',
            data: this.adData.map(d => d.roas)
          },
          {
            name: '클릭수',
            type: 'line',
            data: this.adData.map(d => d.clicks)
          }
        ],
        chart: {
          height: 350,
          type: 'line',
          stacked: false,
          toolbar: {
            show: true
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          width: [1, 4, 4]
        },
        xaxis: {
          categories: this.dateLabels
        },
        yaxis: [
          {
            axisTicks: {
              show: true
            },
            axisBorder: {
              show: true,
              color: '#008FFB'
            },
            labels: {
              style: {
                colors: '#008FFB'
              },
              formatter: (value) => {
                return this.formatCurrency(value);
              }
            },
            title: {
              text: '광고비',
              style: {
                color: '#008FFB'
              }
            },
            tooltip: {
              enabled: true
            }
          },
          {
            seriesName: 'ROAS',
            opposite: true,
            axisTicks: {
              show: true
            },
            axisBorder: {
              show: true,
              color: '#00E396'
            },
            labels: {
              style: {
                colors: '#00E396'
              },
              formatter: (value) => {
                return value + '%';
              }
            },
            title: {
              text: 'ROAS (%)',
              style: {
                color: '#00E396'
              }
            }
          },
          {
            seriesName: '클릭수',
            opposite: true,
            axisTicks: {
              show: true
            },
            axisBorder: {
              show: true,
              color: '#FEB019'
            },
            labels: {
              style: {
                colors: '#FEB019'
              }
            },
            title: {
              text: '클릭수',
              style: {
                color: '#FEB019'
              }
            }
          }
        ],
        tooltip: {
          y: {
            formatter: function (value, { seriesIndex }) {
              if (seriesIndex === 0) {
                return '₩' + value.toLocaleString();
              } else if (seriesIndex === 1) {
                return value + '%';
              }
              return value;
            }
          }
        },
        legend: {
          position: 'top'
        },
        fill: {
          opacity: 1
        },
        colors: ['#008FFB', '#00E396', '#FEB019']
      };

      // 광고 유형별 비용 차트 업데이트
      this.adTypePieChartOption = {
        series: [60, 25, 15],
        chart: {
          type: 'pie',
          height: 350
        },
        labels: ['키워드 광고', '디스플레이 광고', '브랜드 광고'],
        colors: ['#008FFB', '#00E396', '#FEB019'],
        legend: {
          position: 'bottom'
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(1) + '%';
          }
        },
        tooltip: {
          y: {
            formatter: function (value) {
              return value.toFixed(1) + '%';
            }
          }
        }
      };

      // 광고 유형별 ROAS 차트 업데이트
      this.adTypeRoasChartOption = {
        series: [
          {
            name: 'ROAS',
            data: [350, 280, 180]
          }
        ],
        chart: {
          type: 'bar',
          height: 350
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        xaxis: {
          categories: ['키워드 광고', '디스플레이 광고', '브랜드 광고']
        },
        yaxis: {
          title: {
            text: 'ROAS (%)'
          },
          labels: {
            formatter: function (value) {
              return value + '%';
            }
          }
        },
        fill: {
          opacity: 1
        },
        tooltip: {
          y: {
            formatter: function (value) {
              return value + '%';
            }
          }
        },
        colors: ['#00E396']
      };
    },

    getKeywordTrendChartOption(keyword) {
      // 기본 데이터 확인 및 설정
      const clickTrend = keyword.clickTrend || [0, 0, 0, 0, 0, 0, 0];
      const isPositiveTrend = clickTrend.length > 1 && clickTrend[clickTrend.length - 1] > clickTrend[0];

      return {
        series: [
          {
            name: '클릭수',
            data: clickTrend
          }
        ],
        chart: {
          type: 'line',
          height: 40,
          width: 120,
          sparkline: {
            enabled: true
          },
          animations: {
            enabled: false
          }
        },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        tooltip: {
          fixed: {
            enabled: false
          },
          x: {
            show: false
          },
          y: {
            title: {
              formatter: function () {
                return '클릭수:';
              }
            }
          },
          marker: {
            show: false
          }
        },
        colors: [isPositiveTrend ? '#00E396' : '#FF4560'],
        xaxis: {
          categories: Array.from({ length: clickTrend.length }, (_, i) => i + 1)
        }
      };
    },

    getKeywordStatusVariant(keyword) {
      if (keyword.ROAS >= 300) return 'success';
      if (keyword.ROAS >= 200) return 'info';
      if (keyword.ROAS >= 100) return 'warning';
      return 'danger';
    },

    getKeywordStatusText(keyword) {
      if (keyword.ROAS >= 300) return '매우 좋음';
      if (keyword.ROAS >= 200) return '좋음';
      if (keyword.ROAS >= 100) return '보통';
      return '개선 필요';
    },

    getProductRoiVariant(product) {
      if (product.ROAS >= 300) return 'success';
      if (product.ROAS >= 200) return 'info';
      if (product.ROAS >= 100) return 'warning';
      return 'danger';
    },

    getProductRoiText(product) {
      if (product.ROAS >= 300) return '매우 효율적';
      if (product.ROAS >= 200) return '효율적';
      if (product.ROAS >= 100) return '적정';
      return '비효율적';
    },

    formatCurrency(value) {
      return '₩' + value.toLocaleString();
    },

    generateDummyData() {
      // 임시 데이터 생성 (API 연동 전까지 사용)
      this.totalAdCost = 2500000;
      this.roas = 280;
      this.totalClicks = 15000;
      this.avgCtr = 2.8;
      this.adCostGrowth = 5.2;
      this.roasGrowth = 12.5;
      this.clicksGrowth = 8.3;
      this.ctrGrowth = 3.1;

      // 날짜 라벨 생성
      const today = new Date();
      this.dateLabels = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        this.dateLabels.push(date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }));
      }

      // 광고 데이터 생성
      this.adData = this.dateLabels.map((date, index) => {
        const baseCost = 70000 + Math.random() * 30000;
        const baseRoas = 250 + Math.random() * 100;
        const baseClicks = 400 + Math.random() * 200;

        return {
          date,
          cost: Math.round(baseCost + (index / 30) * 20000),
          roas: Math.round(baseRoas + (index / 30) * 50),
          clicks: Math.round(baseClicks + (index / 30) * 100)
        };
      });

      // 키워드 데이터 생성
      this.keywords = [];
      const keywordList = [
        '가방', '신발', '의류', '액세서리', '화장품', '가전제품', '주방용품', '가구',
        '스마트폰', '노트북', '태블릿', '헤드폰', '스피커', '카메라', '게임기', '스포츠용품',
        '장난감', '도서', '문구', '자동차용품', '건강식품', '반려동물용품', '유아용품', '식품'
      ];

      for (let i = 0; i < 20; i++) {
        const impressions = Math.round(1000 + Math.random() * 9000);
        const clicks = Math.round(impressions * (0.01 + Math.random() * 0.04));
        const ctr = (clicks / impressions) * 100;
        const cost = Math.round(clicks * (500 + Math.random() * 500));
        const roas = Math.round(100 + Math.random() * 400);

        // 클릭 추이 생성
        const clickTrend = [];
        for (let j = 0; j < 7; j++) {
          clickTrend.push(Math.round(clicks * (0.7 + Math.random() * 0.6) / 7));
        }

        this.keywords.push({
          키워드: keywordList[i],
          노출수: impressions,
          클릭수: clicks,
          CTR: parseFloat(ctr.toFixed(2)),
          비용: cost,
          ROAS: roas,
          clickTrend
        });
      }

      // 상품 데이터 생성
      this.products = [];
      for (let i = 1; i <= 20; i++) {
        const cost = Math.round(50000 + Math.random() * 150000);
        const roas = Math.round(100 + Math.random() * 400);
        const revenue = Math.round(cost * (roas / 100));
        const impressions = Math.round(5000 + Math.random() * 15000);
        const clicks = Math.round(impressions * (0.01 + Math.random() * 0.04));
        const ctr = (clicks / impressions) * 100;

        this.products.push({
          썸네일: `https://picsum.photos/id/${i + 50}/200/200`,
          판매상품명: `테스트 상품 ${i}`,
          옵션명: `옵션 ${i}`,
          vendorItemId: `VID${i}`,
          productId: `PID${i}`,
          광고비: cost,
          매출: revenue,
          ROAS: roas,
          클릭수: clicks,
          CTR: parseFloat(ctr.toFixed(2))
        });
      }

      // 차트 업데이트
      this.updateCharts();
      this.showDashboard = true;
    }
  },
  watch: {
    selectedPeriod() {
      this.loadData();
    }
  }
};
</script>

<style scoped>
.card {
  margin-bottom: 1.5rem;
}
</style>
